import React, { useEffect, useState } from "react";
import { searchOrders, deleteOrder, getOrderQrCode } from "../../../services/orderService";
import { getAllCustomers } from "../../../services/customerService";
import { Link } from "react-router-dom";
import {
  Table, Button, Badge, Spin, Alert, Pagination, Card, Typography,
  Form, Result, Popconfirm, Space, Tag, Tooltip, Modal
} from "antd";
import { RightCircleFilled, ReloadOutlined, DeleteOutlined, ClockCircleOutlined, WarningOutlined, QrcodeOutlined, DownloadOutlined, PrinterOutlined } from "@ant-design/icons";
import CreateOrderModal from "../../../components/modals/CreateOrderModal";
import OrderFilters from "./components/OrderFilters";
import ActiveFilters from "./components/ActiveFilters";
import PageHeader from "./components/PageHeader";
import { useApiCall } from "../../../hooks/useApiCall";
import { toast } from 'react-toastify';
import { formatDateTime, formatDate } from "../../../utils/formatters";
import { getStatusInfo } from "../../../constants/statusOptions";

const { Text } = Typography;

const QrCodeModal = ({ visible, onClose, orderId, orderReference }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && orderId) {
      fetchQrCode();
    }
    return () => {
      if (qrCodeUrl) {
        URL.revokeObjectURL(qrCodeUrl);
      }
    };
  }, [visible, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQrCode = async () => {
    setLoading(true);
    try {
      const response = await getOrderQrCode(orderId);
      const url = URL.createObjectURL(response.data);
      setQrCodeUrl(url);
    } catch (error) {
      console.error("QR kod yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.href = qrCodeUrl;
      link.download = `siparis_${orderReference}_qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (qrCodeUrl) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Kod - Sipariş ${orderReference}</title>
            <style>
              body { display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0; flex-direction: column; }
              img { max-width: 300px; }
              h2 { font-family: Arial, sans-serif; margin-bottom: 20px; }
            </style>
          </head>
          <body>
            <h2>Sipariş: ${orderReference}</h2>
            <img src="${qrCodeUrl}" alt="QR Kod" />
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleClose = () => {
    if (qrCodeUrl) {
      URL.revokeObjectURL(qrCodeUrl);
      setQrCodeUrl(null);
    }
    onClose();
  };

  return (
    <Modal
      title={`QR Kod - Sipariş ${orderReference}`}
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="download" icon={<DownloadOutlined />} onClick={handleDownload} disabled={!qrCodeUrl}>
          İndir
        </Button>,
        <Button key="print" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!qrCodeUrl}>
          Yazdır
        </Button>,
        <Button key="close" onClick={handleClose}>
          Kapat
        </Button>,
      ]}
      centered
    >
      <div style={{ textAlign: 'center', padding: '20px' }}>
        {loading ? (
          <Spin tip="QR kod yükleniyor..." />
        ) : qrCodeUrl ? (
          <img src={qrCodeUrl} alt="QR Kod" style={{ maxWidth: '100%', height: 'auto' }} />
        ) : (
          <Alert message="QR kod yüklenemedi" type="error" />
        )}
        <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
          Bu QR kodu taratarak sipariş durumunu görüntüleyebilirsiniz.
        </Text>
      </div>
    </Modal>
  );
};

const AllOrdersPage = () => {
  const [ordersData, setOrdersData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterForm] = Form.useForm();
  const [filters, setFilters] = useState({
    status: null,
    customerIds: [],
    dateRange: null,
  });
  const [isFiltered, setIsFiltered] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [selectedOrderForQr, setSelectedOrderForQr] = useState(null);

  // Use the error handling hooks
  const { loading: ordersLoading, error: ordersError, execute: executeOrdersCall } = useApiCall();
  const { loading: companiesLoading, error: companiesError } = useApiCall();
  const { execute: executeDeleteCall } = useApiCall();

  const pageSize = 10;
  const userId = localStorage.getItem("userId");

  const fetchOrders = async (page) => {
    if (!userId) {
      return;
    }

    try {
      let rsqlFilter = isFiltered ? buildRsqlFilter() : null;

      const response = await executeOrdersCall(
        () => searchOrders(userId, rsqlFilter, page - 1, pageSize)
      );

      setOrdersData(response);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrdersData(null);
    }
  };

  const fetchCustomers = async () => {
    try {
      // executeCustomersCall wrapper'ını kullanmadan direkt çağır
      const customersList = await getAllCustomers(userId);

      console.log("Customers received in component:", customersList);
      console.log("Customers type:", typeof customersList);
      console.log("Is array?:", Array.isArray(customersList));

      setCustomers(customersList || []);

    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    setDeletingOrderId(orderId);

    try {
      await executeDeleteCall(
        () => deleteOrder(orderId)
      );

      toast.success('Sipariş başarıyla silindi!');

      // Refresh the orders list
      await fetchOrders(currentPage);

      // If current page becomes empty and we're not on first page, go to previous page
      if (ordersData?.content?.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }

    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error('Sipariş silinirken bir hata oluştu!');
    } finally {
      setDeletingOrderId(null);
    }
  };

  const buildRsqlFilter = () => {
    const filterParts = [];

    if (filters.status) {
      filterParts.push(`status==${filters.status}`);
    }

    if (filters.customerIds && filters.customerIds.length > 0) {
      if (filters.customerIds.length === 1) {
        filterParts.push(`customerId==${filters.customerIds[0]}`);
      } else {
        const customerFilters = filters.customerIds.map(id => `customerId==${id}`).join(',');
        filterParts.push(`(${customerFilters})`);
      }
    }

    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = filters.dateRange[0];
      const endDate = filters.dateRange[1];
      filterParts.push(`orderDate>=${startDate} and orderDate<=${endDate}`);
    }

    return filterParts.length > 0 ? filterParts.join(';') : null;
  };

  useEffect(() => {
    fetchOrders(currentPage);
  }, [currentPage, filters, isFiltered]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCustomers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShowCreateModal = () => setShowCreateModal(true);
  const handleCloseCreateModal = () => setShowCreateModal(false);

  const handleOrderCreated = () => {
    fetchOrders(currentPage);
    toast.success('Sipariş başarıyla oluşturuldu!');
  };

  const handleFilterChange = (values) => {
    const { status, customerIds, dateRange } = values;

    const newFilters = {
      status: status || null,
      customerIds: customerIds || [],
      dateRange: dateRange || null,
    };

    setFilters(newFilters);

    const hasFilters = Boolean(
      status ||
      (customerIds && customerIds.length > 0) ||
      (dateRange && dateRange.length === 2)
    );

    setIsFiltered(hasFilters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    filterForm.resetFields();
    setFilters({
      status: null,
      customerIds: [],
      dateRange: null,
    });
    setIsFiltered(false);
    setCurrentPage(1);
  };

  const handleFilterRemove = (filterType) => {
    const newFilters = { ...filters };
    newFilters[filterType] = filterType === 'customerIds' ? [] : null;

    setFilters(newFilters);
    filterForm.setFieldsValue({ ...filterForm.getFieldsValue(), [filterType]: newFilters[filterType] });

    const hasFilters = Boolean(
      (filterType !== 'status' && newFilters.status) ||
      (filterType !== 'customerIds' && newFilters.customerIds?.length > 0) ||
      (filterType !== 'dateRange' && newFilters.dateRange && newFilters.dateRange.length === 2)
    );

    setIsFiltered(hasFilters);
  };

  const handleRetryOrders = () => {
    fetchOrders(currentPage);
  };

  const handleRetryCustomers = () => {
    fetchCustomers();
  };

  const handleShowQrCode = (order) => {
    setSelectedOrderForQr(order);
    setQrModalVisible(true);
  };

  const handleCloseQrModal = () => {
    setQrModalVisible(false);
    setSelectedOrderForQr(null);
  };

  // Handle case where userId is missing
  if (!userId) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="warning"
          title="Oturum Bilgisi Bulunamadı"
          subTitle="Lütfen tekrar giriş yapın."
          extra={
            <Button type="primary" onClick={() => window.location.href = "/admin/login"}>
              Giriş Yap
            </Button>
          }
        />
      </div>
    );
  }

  // Handle major errors with orders data
  if (ordersError && !ordersLoading && !ordersData) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="error"
          title="Siparişler Yüklenemedi"
          subTitle="Siparişler yüklenirken bir hata oluştu. Lütfen tekrar deneyin."
          extra={
            <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetryOrders}>
              Tekrar Dene
            </Button>
          }
        />
      </div>
    );
  }

  const orders = ordersData?.content || [];

  return (
    <div style={{ padding: "24px" }}>
      <CreateOrderModal
        show={showCreateModal}
        handleClose={handleCloseCreateModal}
        onOrderCreated={handleOrderCreated}
      />

      <QrCodeModal
        visible={qrModalVisible}
        onClose={handleCloseQrModal}
        orderId={selectedOrderForQr?.id}
        orderReference={selectedOrderForQr?.orderReference}
      />

      <PageHeader onCreateOrder={handleShowCreateModal} />

      <Card style={{ marginBottom: 16 }}>
        {companiesError && (
          <Alert
            message="Müşteri listesi yüklenemedi"
            description="Filtreleme için müşteri listesi kullanılamayabilir."
            type="warning"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={handleRetryCustomers}>
                Tekrar Dene
              </Button>
            }
          />
        )}

        <OrderFilters
          form={filterForm}
          customers={customers}
          loadingCustomers={companiesLoading}
          onFinish={handleFilterChange}
          onReset={handleResetFilters}
        />
      </Card>

      <Card>
        {isFiltered && (
          <ActiveFilters
            filters={filters}
            onRemoveFilter={handleFilterRemove}
          />
        )}

        {ordersLoading ? (
          <div style={{ textAlign: "center", padding: "48px" }}>
            <Spin tip="Siparişler yükleniyor..." size="large" />
          </div>
        ) : orders.length === 0 ? (
          <Alert
            message="Sipariş bulunamadı"
            description={isFiltered ? "Filtrelere uygun sipariş bulunamadı. Filtreleri değiştirmeyi deneyin." : "Henüz hiç sipariş oluşturulmamış."}
            type="info"
            showIcon
          />
        ) : (
          <>
            <OrdersTable
              orders={orders}
              onDeleteOrder={handleDeleteOrder}
              deletingOrderId={deletingOrderId}
              onShowQrCode={handleShowQrCode}
            />

            <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={ordersData?.totalElements || 0}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
              />
            </div>
          </>
        )}

        {!ordersLoading && ordersData && (
          <div style={{ textAlign: "right", marginTop: 16 }}>
            <Text type="secondary">
              Toplam sipariş: {ordersData?.totalElements || 0}
            </Text>
          </div>
        )}
      </Card>
    </div>
  );
};

const OrdersTable = ({ orders, onDeleteOrder, deletingOrderId, onShowQrCode }) => {
  const getStatusBadge = (status) => {
    const { color, text } = getStatusInfo(status, "order");
    return <Badge status={color} text={text} />;
  };

  const columns = [
    {
      title: "Şirket Adı",
      dataIndex: "companyName",
      key: "companyName",
      render: (text) => text || "-",
    },
    {
      title: "Sipariş No",
      dataIndex: "orderReference",
      key: "orderReference",
    },
    {
      title: "Sipariş Tarihi",
      dataIndex: "orderDate",
      key: "orderDate",
      render: (date) => formatDateTime(date),
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      render: getStatusBadge,
    },
    {
      title: "Teslim Tarihi",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (dueDate, record) => {
        if (!dueDate) return "-";
        const formattedDateStr = formatDate(dueDate);
        if (record.isOverdue) {
          return (
            <Tooltip title="Gecikmiş sipariş">
              <Tag color="red" icon={<WarningOutlined />}>
                {formattedDateStr}
              </Tag>
            </Tooltip>
          );
        }
        if (record.isDueSoon) {
          return (
            <Tooltip title="Teslim yaklaşıyor (3 gün içinde)">
              <Tag color="orange" icon={<ClockCircleOutlined />}>
                {formattedDateStr}
              </Tag>
            </Tooltip>
          );
        }
        return formattedDateStr;
      },
    },
    {
      title: "Notlar",
      dataIndex: "notes",
      key: "notes",
      render: (text) => text || "-",
    },
    {
      title: "Eylemler",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="QR Kod">
            <Button
              size="small"
              icon={<QrcodeOutlined />}
              onClick={() => onShowQrCode(record)}
            />
          </Tooltip>

          <Link to={`/admin/order-items/${record.id}`}>
            <Button type="primary" size="small" icon={<RightCircleFilled />}>
              Detaylar
            </Button>
          </Link>

          <Popconfirm
            title="Siparişi Sil"
            description="Bu siparişi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
            onConfirm={() => onDeleteOrder(record.id)}
            okText="Evet, Sil"
            cancelText="İptal"
            okType="danger"
            placement="topRight"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingOrderId === record.id}
              disabled={deletingOrderId === record.id}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={orders}
      rowKey="id"
      pagination={false}
      bordered
      scroll={{ x: true }}
    />
  );
};

export default AllOrdersPage;